<template>
  <div class="event-details">
    <Progression v-if="loading"></Progression>
    <div v-else class="event-contents">
      <div v-if="!!initEvent" class="event-header">
        <v-img
          :key="previewKey"
          :src="imagePreview"
          style="width:100%; max-height:200px; object-fit:cover;"
        ></v-img>
      </div>

      <div v-if="!formOnly" class="subtitle-1">
        <h3 v-if="initEvent.cancelled" style="color:#ed1c24;">
          {{ $t("scheduler.participant.cancelled") }}
        </h3>
        <div v-else-if="$store.getters['site/user'].role !== 'student'">
          <div v-if="!!participation">
            <h3 style="color:green">
              {{ $t("scheduler.participant.participate") }}
            </h3>
          </div>
          <h3 v-else style="color:#ed1c24;">
            {{ $t("scheduler.participant.not-participating") }}
          </h3>
        </div>
      </div>

      <v-container fill-height fluid>
        <v-layout align-center justify-center>
          <v-flex md12>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-text-field
                v-model="event.title"
                :readonly="!canEdit()"
                prepend-icon="short_text"
                :rules="$rules('scheduler.event.title')"
                :label="$t('scheduler.event.fields.title')"
                required
              ></v-text-field>
              <v-checkbox
                v-if="canEdit()"
                v-model="event.allDay"
                :label="$t('scheduler.event.fields.all-day')"
              />

              <v-datetime-picker
                v-if="canEdit()"
                v-model="event.start"
                time-format="HH:mm:ss"
                :label="$t('scheduler.event.fields.start')"
                :text-field-props="{
                  prependIcon: 'schedule'
                }"
              ></v-datetime-picker>
              <v-text-field
                v-else
                v-model="event.start"
                readonly
                :label="$t('scheduler.event.fields.start')"
                prepend-icon="schedule"
              ></v-text-field>
              <v-datetime-picker
                v-if="canEdit()"
                v-model="event.end"
                time-format="HH:mm:ss"
                :label="$t('scheduler.event.fields.end')"
                :text-field-props="{
                  prependIcon: 'schedule'
                }"
              ></v-datetime-picker>
              <v-text-field
                v-else
                v-model="event.end"
                readonly
                prepend-icon="schedule"
                :label="$t('scheduler.event.fields.end')"
              ></v-text-field>
              <v-text-field
                v-if="canEdit()"
                v-model="event.image"
                prepend-icon="link"
                :rules="$rules('app.image')"
                :label="$t('scheduler.event.fields.image')"
                @change="updateImagePreview"
              ></v-text-field>
              <v-text-field
                v-if="canEdit()"
                v-model="event.abstract"
                :readonly="!canEdit()"
                prepend-icon="short_text"
                :rules="$rules('scheduler.event.shortDescription')"
                :label="$t('scheduler.event.fields.abstract')"
              ></v-text-field>
              <v-textarea
                v-model="event.description"
                :readonly="!canEdit()"
                prepend-icon="notes"
                auto-grow
                :rules="$rules('scheduler.event.longDescription')"
                :label="$t('scheduler.event.fields.description')"
              ></v-textarea>

              <div v-if="!!participation">
                <h3>{{ $t("scheduler.participant.details") }}</h3>
                <v-text-field
                  v-model="participation.reason"
                  readonly
                  prepend-icon="short_text"
                  :label="$t('scheduler.participant.fields.reason')"
                ></v-text-field>
                <v-text-field
                  v-model="participation.notice"
                  readonly
                  prepend-icon="short_text"
                  :label="$t('scheduler.participant.fields.notice')"
                ></v-text-field>
              </div>
            </v-form>
            <v-card-actions v-if="!formOnly">
              <v-btn outlined @click="back">
                {{ $t("app.buttons.back") }}
              </v-btn>

              <v-spacer></v-spacer>

              <DeleteButton
                v-if="!!initEvent && canEdit() && !initEvent.cancelled"
                :message="$t('scheduler.event.messages.delete')"
                :name="$t('scheduler.event.name')"
                :soft="false"
                :endpoint="'/events/' + initEvent.id"
                @deleted="back"
              ></DeleteButton>

              <v-btn v-if="canEdit()" dark color="primary" @click="submit">
                {{ $t("app.buttons.submit") }}
              </v-btn>
            </v-card-actions>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
  </div>
</template>

<script>
import Progression from "@/components/Progression";
import DeleteButton from "@/components/utilities/DeleteButton";
import { mapState } from "vuex";
import adaptersIn from "@/services/data-adapter/incoming/scheduler";
import adaptersOut from "@/services/data-adapter/outgoing/scheduler";

export default {
  components: {
    Progression,
    DeleteButton
  },

  props: {
    passedEvent: {
      type: Object,
      default: null
    },
    formOnly: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    initEvent: null,
    imagePreview: null,
    previewKey: 0,
    eventLoading: true,
    participationLoading: true,
    options: {
      tabDateTitle: "Date",
      tabTimeTitle: "Time",
      icon: "event",
      iconTime: "av_timer",
      closeOnDateClick: false,
      useSeconds: false,
      clearable: true
    },
    participation: null,
    role: "",
    valid: true,
    event: null
  }),

  computed: {
    loading: function() {
      return this.eventLoading || this.participationLoading;
    },
    filled: function() {
      return (
        this.eventDifferent &&
        this.event.title &&
        this.event.start &&
        this.event.abstract
      );
    },
    eventDifferent: function() {
      return (
        this.initEvent.title != this.event.title ||
        this.initEvent.start != this.event.start ||
        this.initEvent.end != this.event.end ||
        this.initEvent.allDay != this.event.allDay ||
        this.initEvent.image != this.event.image ||
        this.initEvent.abstract != this.event.abstract ||
        this.initEvent.description != this.event.description
      );
    },
    ...mapState("toolbox/advanced-forms", ["validating"])
  },
  watch: {
    validating: function(val) {
      if (val) {
        const valid = this.$refs.form.validate();
        this.$store.dispatch("toolbox/advanced-forms/submitValidation", valid);
        this.$emit("validated", { eventId: this.initEvent.id, valid: valid });
      }
    },

    event: {
      handler: function() {
        this.$emit("change", this.event);
      },
      deep: true
    }
  },

  mounted: function() {
    this.$nextTick(async function() {
      this.initEvent = this.passedEvent;
      this.setEvent();
      this.eventLoading = false;

      // Load the preview
      this.updateImagePreview();

      this.participationLoading = !this.formOnly;
      if (this.participationLoading) {
        this.getParticipation();
      }
    });
  },

  methods: {
    setEvent() {
      this.event = JSON.parse(JSON.stringify(this.initEvent));
      this.eventLoading = false;
    },

    updateImagePreview() {
      this.imagePreview = this.event.image
        ? this.event.image
        : require("@/assets/images/event-placeholder.png");
      // Force the preview to re-render
      this.previewKey++;
    },

    getParticipation() {
      this.participationLoading = true;
      return new Promise((resolve, reject) => {
        this.$http
          .get(
            "/events/" +
              this.$route.params.eventId +
              "/participation/" +
              this.$store.getters["site/user"].id
          )
          .then(response => {
            if (response.data.participation) {
              adaptersIn
                .participation(response.data.participation)
                .then(res => (this.participation = res));
            }
            this.participationLoading = false;
            return resolve(true);
          })
          .catch(error => {
            if (process.env.NODE_ENV !== "production") {
              console.log(error);
            }
            return reject(false);
          });
      });
    },

    canEdit() {
      const role = this.$store.getters["site/user"].role;
      return (
        !this.initEvent.cancelled && (role === "admin" || role === "teacher")
      );
    },

    async submit() {
      if (this.$refs.form.validate()) {
        adaptersOut.event(this.event).then(res => {
          const event = res;
          this.$http
            .put(
              "/events/" + this.initEvent.id,
              { event },
              { withCredentials: true, credentials: "include", mode: "cors" }
            )
            .then(request => this.submitSuccessful(request.data))
            .catch(error => this.submitFailed(error));
        });
      }
    },

    submitSuccessful(req) {
      this.$store.dispatch(
        "system/setStatus",
        {
          type: "success",
          message: this.$t("app.actions.success.update")
        },
        { root: true }
      );
      this.reload();
    },

    submitFailed(error) {
      if (process.env.NODE_ENV !== "production") {
        console.log(error.response);
      }
      this.$store.dispatch(
        "system/setStatus",
        {
          type: "error",
          message: this.$t("app.actions.failed.update")
        },
        { root: true }
      );
    },

    reload() {
      this.$router.go();
    },

    back() {
      this.$router.go(-1);
    }
  }
};
</script>
